import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import {
	Button,
	Container,
	Paper,
	Stack,
	Typography,
	TableContainer,
	Table,
	TableRow,
	TableHead,
	TableCell,
	TableBody,
	TextField,
	Grid,
	Divider,
	Chip
} from '@mui/material';
import ReactToPrint from 'react-to-print';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faChevronCircleLeft,
	faCircleCheck,
	faLockOpen,
	faPrint
} from '@fortawesome/free-solid-svg-icons';
import { DataContext } from '../../../../context/DataContext';
import { getRuta, isAccess, simpleMessage } from '../../../../helpers/Helpers';
import { getToken } from '../../../../api/Account';
import { PrintReport } from '../../../../components/PrintReport';
import {
	cerrarArqueoSucursalAsync,
	getArqueoSucursalByIdAsync,
	getDataArqueoSucursalAsync,
	getDenominationsAsync,
	reabrirArqueoSucursalAsync
} from '../../../../api/ArqueoCaja';
import { getTipoCambioHoyAsync } from '../../../../api/TipoCambio';
import moment from 'moment';

/* ================== HELPERS ================== */
const formatter = new Intl.NumberFormat('es-NI', {
	style: 'currency',
	currency: 'NIO'
});

const toNumber = (value) => Number(value) || 0;
const round2 = (value) => Math.round(value * 100) / 100;

// Billetaje editable de un arqueo reabierto: parte de las denominaciones y
// precarga el conteo que ya estaba guardado para que caja solo ajuste lo que cambió.
const buildDetalle = (denominaciones, detallesGuardados = []) =>
	denominaciones.map((den) => {
		const guardado = detallesGuardados.find((d) => d.demonimacion?.id === den.id);
		return {
			id: den.id,
			description: den.description,
			valor: den.valor,
			cantidad: guardado ? guardado.cantidad : 0,
			total: guardado ? guardado.total : 0
		};
	});

const ArqueoSucursalDetails = () => {
	const [arqueo, setArqueo] = useState([]);
	// console.log('🚀 ~ arqueo:', arqueo);

	// Solo para un arqueo reabierto: sumas de los arqueos de usuario cerrados del día,
	// billetaje recontado, tipo de cambio y observaciones.
	const [dataArqueo, setDataArqueo] = useState(null);
	const [detalleArqueo, setDetalleArqueo] = useState([]);
	const [tcCompra, setTcCompra] = useState(0);
	const [observaciones, setObservaciones] = useState('');

	const [localReload, setLocalReload] = useState(false);

	const { id } = useParams();

	const { setIsLoading, setIsUnautorized, access } = useContext(DataContext);

	const token = getToken();
	let ruta = getRuta();
	let navigate = useNavigate();

	const isCerrado = arqueo?.estadoArqueo === 2;
	const isAbierto = arqueo?.estadoArqueo === 1;

	// Cerrado: se muestra lo guardado. Abierto: los montos guardados quedaron congelados
	// al crearlo, así que se muestran las sumas actuales de los arqueos de usuario del día.
	const resumen = isAbierto
		? {
				tipoCambio: tcCompra,
				saldoInicial: toNumber(dataArqueo?.montoSaldoInicial),
				recuperacion: toNumber(dataArqueo?.montoRecuperacionEfectivo),
				recuperacionTransferencia: toNumber(dataArqueo?.montoRecuperacionTransferencia),
				totalIngresos: toNumber(dataArqueo?.montoTotalIngresos),
				depositos: toNumber(dataArqueo?.montoDepositos),
				montoGastos: toNumber(dataArqueo?.montoGastos),
				montoDesembolso: toNumber(dataArqueo?.montoDesembolsos),
				totalEgresos: toNumber(dataArqueo?.montoTotalEgresos)
		  }
		: {
				tipoCambio: toNumber(arqueo?.tipoCambio),
				saldoInicial: toNumber(arqueo?.saldoInicial),
				recuperacion: toNumber(arqueo?.recuperacion),
				recuperacionTransferencia: toNumber(arqueo?.recuperacionTransferencia),
				totalIngresos: toNumber(arqueo?.totalIngresos),
				depositos: toNumber(arqueo?.depositos),
				montoGastos: toNumber(arqueo?.montoGastos),
				montoDesembolso: toNumber(arqueo?.montoDesembolso),
				totalEgresos: toNumber(arqueo?.totalEgresos)
		  };

	const totalBilletaje = isAbierto
		? round2(detalleArqueo.reduce((sum, item) => sum + item.total, 0))
		: toNumber(arqueo?.efectivo);
	// Solo la recuperación en efectivo representa dinero físico en caja;
	// los abonos por transferencia no forman parte de la entrega.
	const entrega =
		resumen.saldoInicial +
		resumen.recuperacion -
		(resumen.montoGastos + resumen.montoDesembolso + resumen.depositos);
	const diferencia = isAbierto ? round2(totalBilletaje - entrega) : toNumber(arqueo?.diferencia);

	useEffect(() => {
		getArqueoById();
	}, [localReload]);

	const handleError = (result) => {
		setIsLoading(false);
		if (result.error === 'Unauthorized') {
			setIsUnautorized(true);
			return true;
		}
		simpleMessage(result.error, 'error');
		return true;
	};

	const getArqueoById = async () => {
		setIsLoading(true);
		const result = await getArqueoSucursalByIdAsync(token, id);
		if (!result.statusResponse && handleError(result)) return;

		const data = result.data.result;
		setArqueo(data);
		setObservaciones(data.observaciones || '');

		if (data.estadoArqueo === 1) {
			await loadDatosAbierto(data);
		}
		setIsLoading(false);
	};

	// Un arqueo reabierto se cierra recontando: hacen falta las sumas del día,
	// las denominaciones para el billetaje y el tipo de cambio.
	const loadDatosAbierto = async (data) => {
		const [sumas, denominaciones, tc] = await Promise.all([
			getDataArqueoSucursalAsync(token, {
				sucursalId: data.sucursal.id,
				fecha: moment(data.fechaArqueo).format('YYYY-MM-DD')
			}),
			getDenominationsAsync(token),
			getTipoCambioHoyAsync(token)
		]);
		if (!sumas.statusResponse) return handleError(sumas);
		if (!denominaciones.statusResponse) return handleError(denominaciones);
		if (!tc.statusResponse) return handleError(tc);

		setDataArqueo(sumas.data.result);
		// Se respeta el tipo de cambio con que se hizo el arqueo; si no tiene, el de hoy.
		setTcCompra(toNumber(data.tipoCambio) || toNumber(tc.data.compra));
		setDetalleArqueo(buildDetalle(denominaciones.data.result, data.aqueoSucursalDetails));
	};

	const onEditDetalle = useCallback(
		(detalleId, valor, cantidad) => {
			const qty = toNumber(cantidad);
			setDetalleArqueo((prev) =>
				prev.map((item) => {
					if (item.id !== detalleId) return item;
					let total = valor * qty;
					if (item.id === 2) total *= tcCompra; // Ajuste para moneda extranjera
					return { ...item, cantidad: qty, total: round2(total) };
				})
			);
		},
		[tcCompra]
	);

	const onCloseArqueo = async () => {
		setIsLoading(true);
		const result = await cerrarArqueoSucursalAsync(token, id, {
			aqueoUserDetails: detalleArqueo.map((d) => ({
				demonimacionId: d.id,
				cantidad: toNumber(d.cantidad),
				total: d.total
			})),
			tipoCambio: tcCompra,
			observaciones: observaciones.toUpperCase()
		});
		if (!result.statusResponse && handleError(result)) return;

		setLocalReload(!localReload);
		simpleMessage('Arqueo cerrado con éxito', 'success');
		setIsLoading(false);
	};

	const onReopenArqueo = async () => {
		setIsLoading(true);
		const result = await reabrirArqueoSucursalAsync(token, id);
		if (!result.statusResponse && handleError(result)) return;

		setLocalReload(!localReload);
		simpleMessage('Arqueo reabierto con éxito', 'success');
		setIsLoading(false);
	};

	const compRef = useRef();

	const pageStyle = `
  @media print {
    html, body {
      height: initial !important;
      overflow: initial !important;
      -webkit-print-color-adjust: exact;
    }
  }
  @media all {
    .pagebreak {
      display: none;
    }
  }
  @media print {
    .pagebreak {
      page-break-before: always;
    }
  }
  @page {   margin: 8mm; }
`;

	return arqueo.length === 0 ? null : (
		<div>
			<Container maxWidth="xl">
				<Paper sx={{ p: 2 }}>
					<Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={1}>
						<Button
							onClick={() => {
								navigate(`${ruta}/cashregister`);
							}}
							style={{
								color: '#2979ff',
								borderColor: '#2979ff'
							}}
							variant="outlined"
						>
							<FontAwesomeIcon
								style={{ marginRight: 10, fontSize: 20 }}
								icon={faChevronCircleLeft}
							/>
							Regresar
						</Button>

						<Typography color={'#00a152'} variant="h5" fontWeight={'bold'}>
							Detalle Arqueo: {arqueo.sucursal.description}
						</Typography>
					</Stack>

					{isAbierto && (
						<Typography variant="body2" color="#ffc400" fontWeight="bold" mt={1}>
							Arqueo reabierto: los montos se recalculan con los arqueos de usuario
							cerrados del día. Recuente el efectivo y cierre el arqueo.
						</Typography>
					)}

					<hr />

					<Grid container spacing={2}>
						<Grid item sm={12} md={6}>
							<Paper sx={{ p: 2 }}>
								<Stack spacing={1}>
									<Stack
										direction="row"
										justifyContent="space-between"
										spacing={2}
										alignItems="center"
									>
										<Typography color={'#2979ff'} fontWeight={'bold'}>
											{` Arqueo #: ${arqueo.id} `}
										</Typography>

										<Stack spacing={1} direction="row">
											<Stack spacing={1}>
												<Chip
													size="small"
													color="primary"
													variant="outlined"
													label={`Fecha creación: ${moment(arqueo.fechaCreacion).format('L')}`}
												/>
												<Chip
													size="small"
													color="primary"
													variant="outlined"
													label={`Fecha arqueada: ${moment(arqueo.fechaArqueo).format('L')}`}
												/>
												<Chip
													size="small"
													color={isCerrado ? 'success' : 'warning'}
													label={isCerrado ? 'Cerrado' : 'Abierto'}
												/>
											</Stack>
											{/* <Typography color={'#2979ff'} fontWeight={'bold'}>
												Fecha:
											</Typography>
											<Typography color={'#00a152'} fontWeight={'bold'}>
												{moment(arqueo.fechaArqueo).format('L')}
											</Typography> */}
										</Stack>
									</Stack>

									<hr />

									<Stack
										direction="row"
										justifyContent="space-between"
										color="#ffc400"
										mt={2}
									>
										<Typography fontWeight="bold">Tipo de Cambio</Typography>
										<Typography fontWeight="bold">
											{formatter.format(resumen.tipoCambio)}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">Saldo Inicial</Typography>
										<Typography fontWeight="bold">
											{formatter.format(resumen.saldoInicial)}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">
											Recuperación en Efectivo
										</Typography>
										<Typography fontWeight="bold">
											{formatter.format(resumen.recuperacion)}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">
											Recuperación en Depósitos
										</Typography>
										<Typography fontWeight="bold">
											{formatter.format(resumen.recuperacionTransferencia)}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">Total Ingresos</Typography>
										<Typography fontWeight="bold" color="#00a152">
											{formatter.format(resumen.totalIngresos)}
										</Typography>
									</Stack>

									<hr />

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">Monto Depositos</Typography>
										<Typography fontWeight="bold">
											{formatter.format(resumen.depositos)}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">Monto Gastos</Typography>
										<Typography fontWeight="bold">
											{formatter.format(resumen.montoGastos)}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">Monto Desembolsos</Typography>
										<Typography fontWeight="bold">
											{formatter.format(resumen.montoDesembolso)}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">Total Egresos</Typography>
										<Typography fontWeight="bold" color="#f50057">
											{formatter.format(resumen.totalEgresos)}
										</Typography>
									</Stack>

									<hr />

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">Entrega</Typography>
										<Typography fontWeight="bold" color="#2979ff">
											{formatter.format(entrega)}
										</Typography>
									</Stack>

									<Stack direction="row" justifyContent="space-between">
										<Typography fontWeight="bold">Diferencia</Typography>
										<Typography
											fontWeight="bold"
											color={diferencia === 0 ? '#00a152' : '#ffc400'}
										>
											{formatter.format(diferencia)}
										</Typography>
									</Stack>

									{isAbierto ? (
										<TextField
											fullWidth
											size="small"
											label="Observaciones"
											value={observaciones}
											onChange={(e) =>
												setObservaciones(e.target.value.toLocaleUpperCase())
											}
											multiline
										/>
									) : (
										<Stack direction="row" justifyContent="space-between">
											<Typography fontWeight="bold">Observaciones</Typography>
											<Typography fontWeight="bold" color="#2979ff">
												{arqueo.observaciones || ''}
											</Typography>
										</Stack>
									)}
								</Stack>
							</Paper>
						</Grid>

						<Grid item sm={12} md={6}>
							<Paper sx={{ p: 2 }}>
								<Stack spacing={1}>
									<Stack direction="row" justifyContent="space-between" mb={0}>
										<Typography color={'#00a152'} fontWeight={'bold'}>
											Detalle Billetaje
										</Typography>
										{isAbierto && (
											<Typography variant="body2" color="#ffc400">
												Compra: {formatter.format(tcCompra)}
											</Typography>
										)}
									</Stack>

									<hr />

									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell sx={{ py: 0.25 }}>
														Denominacion
													</TableCell>
													<TableCell align="center" sx={{ py: 0.25 }}>
														Billetaje
													</TableCell>
													<TableCell align="center" sx={{ py: 0.25 }}>
														C$
													</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{isAbierto
													? detalleArqueo.map((row) => (
															<TableRow
																key={row.id}
																sx={{
																	'& td': {
																		py: 0.25,
																		px: 1
																	}
																}}
															>
																<TableCell>{row.description}</TableCell>
																<TableCell align="center" width={150}>
																	<TextField
																		size="small"
																		value={row.cantidad}
																		onChange={(e) =>
																			onEditDetalle(
																				row.id,
																				row.valor,
																				e.target.value
																			)
																		}
																		inputProps={{
																			style: {
																				padding: '2px 6px',
																				height: 26,
																				textAlign: 'center'
																			}
																		}}
																	/>
																</TableCell>
																<TableCell align="center">
																	{formatter.format(row.total)}
																</TableCell>
															</TableRow>
													  ))
													: arqueo.aqueoSucursalDetails.map((row) => {
															const { cantidad, demonimacion, id, total } =
																row;

															return (
																<TableRow
																	sx={{
																		'& td': {
																			py: 0.6,
																			px: 1
																		}
																	}}
																	key={id}
																>
																	<TableCell align="left">
																		{demonimacion.description}
																	</TableCell>
																	<TableCell align="center">
																		{cantidad}
																	</TableCell>
																	<TableCell align="center">
																		{formatter.format(total)}
																	</TableCell>
																</TableRow>
															);
													  })}

												<TableRow
													sx={{
														'& td': {
															py: 0.25,
															px: 1,
															fontWeight: 'bold',
															color: '#00a152'
														}
													}}
												>
													<TableCell colSpan={2}>
														TOTAL BILLETAJE
													</TableCell>
													<TableCell align="center">
														{formatter.format(totalBilletaje)}
													</TableCell>
												</TableRow>
											</TableBody>
										</Table>
									</TableContainer>
								</Stack>
							</Paper>
						</Grid>

						<Grid item xs={12}>
							<Stack direction="row" spacing={2}>
								<ReactToPrint
									trigger={() => {
										return (
											<Button
												style={{
													color: '#2979ff',
													borderColor: '#2979ff',
													padding: 13,
													borderRadius: 30
												}}
												variant="outlined"
												fullWidth
											>
												<FontAwesomeIcon
													style={{
														marginRight: 10,
														fontSize: 20
													}}
													icon={faPrint}
												/>
												Imprimir Arqueo
											</Button>
										);
									}}
									content={() => compRef.current}
									pageStyle={pageStyle}
								/>

								{isAbierto && isAccess(access, 'ACAJA CREATE') && (
									<Button
										onClick={onCloseArqueo}
										style={{
											color: '#00a152',
											borderColor: '#00a152',
											padding: 13,
											borderRadius: 30
										}}
										variant="outlined"
										fullWidth
									>
										<FontAwesomeIcon
											style={{
												marginRight: 10,
												fontSize: 20
											}}
											icon={faCircleCheck}
										/>
										Cerrar Arqueo
									</Button>
								)}

								{isCerrado && isAccess(access, 'REABRIR ARQUEO') && (
									<Button
										onClick={onReopenArqueo}
										style={{
											color: '#00a152',
											borderColor: '#00a152',
											padding: 13,
											borderRadius: 30
										}}
										variant="outlined"
										fullWidth
									>
										<FontAwesomeIcon
											style={{
												marginRight: 10,
												fontSize: 20
											}}
											icon={faLockOpen}
										/>
										Reabrir Arqueo
									</Button>
								)}
							</Stack>
						</Grid>
					</Grid>
				</Paper>
			</Container>

			<div
				style={{
					display: 'none'
				}}
			>
				<PrintReport
					ref={compRef}
					titulo={'Arqueo de Sucursal'}
					fecha={`Fecha: ${moment().format('L')}`}
				>
					<Stack spacing={1} direction="row" justifyContent="space-between">
						<Typography color={'#2979ff'} fontWeight={'bold'}>
							{` Arqueo #: ${arqueo.id} `}
						</Typography>

						<Stack spacing={1} direction="row">
							<Typography color={'#2979ff'} fontWeight={'bold'}>
								Fecha:
							</Typography>
							<Typography color={'#00a152'} fontWeight={'bold'}>
								{moment(arqueo.fechaCreacion).format('L')}
							</Typography>
						</Stack>
					</Stack>

					<Divider
						sx={{
							my: 1,
							'@media print': {
								borderBottomWidth: 2,
								borderColor: '#000'
							}
						}}
					/>

					<Grid container spacing={2}>
						<Grid item sm={6}>
							<Stack
								direction="row"
								justifyContent="space-between"
								color="#ffc400"
								mt={2}
							>
								<Typography fontWeight="bold">Tipo de Cambio</Typography>
								<Typography fontWeight="bold">
									{formatter.format(resumen.tipoCambio)}
								</Typography>
							</Stack>

							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Saldo Inicial</Typography>
								<Typography fontWeight="bold">
									{formatter.format(resumen.saldoInicial)}
								</Typography>
							</Stack>

							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Recuperación en Efectivo</Typography>
								<Typography fontWeight="bold">
									{formatter.format(resumen.recuperacion)}
								</Typography>
							</Stack>

							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Recuperación en Depósitos</Typography>
								<Typography fontWeight="bold">
									{formatter.format(resumen.recuperacionTransferencia)}
								</Typography>
							</Stack>

							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Total Ingresos</Typography>
								<Typography fontWeight="bold" color="#00a152">
									{formatter.format(resumen.totalIngresos)}
								</Typography>
							</Stack>

							<Divider
								sx={{
									my: 1,
									'@media print': {
										borderBottomWidth: 2,
										borderColor: '#000'
									}
								}}
							/>
							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Entrega</Typography>
								<Typography fontWeight="bold" color="#00a152">
									{formatter.format(entrega)}
								</Typography>
							</Stack>
						</Grid>

						<Grid item sm={6}>
							<Stack direction="row" justifyContent="space-between" mt={2}>
								<Typography fontWeight="bold">Monto Depositos</Typography>
								<Typography fontWeight="bold">
									{formatter.format(resumen.depositos)}
								</Typography>
							</Stack>

							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Monto Gastos</Typography>
								<Typography fontWeight="bold">
									{formatter.format(resumen.montoGastos)}
								</Typography>
							</Stack>

							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Total Desembolsos</Typography>
								<Typography fontWeight="bold">
									{formatter.format(resumen.montoDesembolso)}
								</Typography>
							</Stack>

							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Total Egresos</Typography>
								<Typography fontWeight="bold" color="#f50057">
									{formatter.format(resumen.totalEgresos)}
								</Typography>
							</Stack>
							<Divider
								sx={{
									my: 1,
									'@media print': {
										borderBottomWidth: 2,
										borderColor: '#000'
									}
								}}
							/>
							<Stack direction="row" justifyContent="space-between">
								<Typography fontWeight="bold">Diferencia</Typography>
								<Typography fontWeight="bold" color="#f50057">
									{formatter.format(diferencia)}
								</Typography>
							</Stack>
						</Grid>

						<Grid item sm={12}>
							<Typography fontWeight="bold">{`Observaciones: ${arqueo.observaciones}`}</Typography>
						</Grid>

						<Grid item sm={12}>
							<TableContainer>
								<Table aria-label="simple table" size="small">
									<TableHead>
										<TableRow>
											<TableCell
												align="left"
												className="text-primary fw-bold"
											>
												Denominacion
											</TableCell>
											<TableCell
												align="center"
												className="text-primary fw-bold"
											>
												Billetaje
											</TableCell>
											<TableCell
												align="center"
												className="text-primary fw-bold"
											>
												C$
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{arqueo.aqueoSucursalDetails.map((row) => {
											const { cantidad, demonimacion, id, total } = row;

											return (
												<TableRow key={id}>
													<TableCell align="left" className="text-dark">
														{demonimacion.description}
													</TableCell>
													<TableCell align="center" className="text-dark">
														{cantidad}
													</TableCell>
													<TableCell align="center" className="text-dark">
														{formatter.format(total)}
													</TableCell>
												</TableRow>
											);
										})}

										<TableRow
											sx={{
												'& td': {
													py: 0.25,
													px: 1,
													fontWeight: 'bold',
													color: '#00a152'
												}
											}}
										>
											<TableCell colSpan={2}>TOTAL BILLETAJE</TableCell>
											<TableCell align="center">
												{formatter.format(arqueo.efectivo || 0)}
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</TableContainer>
						</Grid>
					</Grid>

					<Typography
						color={'#2979ff'}
						fontWeight={'bold'}
						align="center"
						style={{ textDecoration: 'overline', marginTop: 150 }}
					>
						{` Creado Por #: ${arqueo.usuarioCreacion.fullName} `}
					</Typography>
				</PrintReport>
			</div>
		</div>
	);
};

export default ArqueoSucursalDetails;
